/**
 * Launch Portal — public section components. Rendered inside the
 * existing Discovery Portal layout once HQ has finalised the launch
 * portal (server sets ``launch_portal`` on the /public/discovery
 * payload).
 *
 * Auth is inherited from the Discovery Portal: the visitor already
 * holds the ``dp_pw`` cookie (or the per-slug session cookie), so
 * we can POST to ``/public/launch-portal/<slug>/…`` with ``credentials:
 * "include"``. No token / password / login is asked for again.
 *
 * All 8 sections are exported from this single file to keep the
 * import surface small on DiscoveryPortalPage.jsx.
 */
import React, { useCallback, useEffect, useState } from "react";
import { CheckCircle2, Circle, Loader2, Lock, Upload, ExternalLink, MailCheck, XCircle } from "lucide-react";
import PortalPdfViewer from "@/components/pdf/PortalPdfViewer";
import { useDbsDirtyGate } from "@/components/launch-portal/DbsDirtyGate";

const API = process.env.REACT_APP_BACKEND_URL;

// Public launch-portal fetch — always with credentials so the
// discovery session cookie hitches a ride.
async function submitJson(slug, path, body) {
  const res = await fetch(`${API}/api/public/launch-portal/${slug}/${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try { const j = await res.json(); detail = j.detail || detail; } catch { /* noop */ }
    throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  }
  return res.json();
}

async function submitMultipart(slug, path, formData) {
  const res = await fetch(`${API}/api/public/launch-portal/${slug}/${path}`, {
    method: "POST",
    credentials: "include",
    body: formData,
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try { const j = await res.json(); detail = j.detail || detail; } catch { /* noop */ }
    throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  }
  return res.json();
}

async function fetchJson(slug, path) {
  const res = await fetch(`${API}/api/public/launch-portal/${slug}/${path}`, {
    method: "GET",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}


// ================================================================
// SubmittedLock — shared "this section is locked" indicator once
// the franchisee has submitted their data.
// ================================================================
export function SubmittedLock({ text, testId }) {
  return (
    <div
      className="mt-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
      data-testid={testId || "launch-section-locked"}
    >
      <Lock className="w-4 h-4" />
      <span>{text || "Submitted — this section is now locked."}</span>
    </div>
  );
}


// ================================================================
// 6.1 Contract Details
// ================================================================
export function ContractDetailsSection({ slug, launchPortal, onSubmitted }) {
  const existing = launchPortal.contract_details;
  const submitted = !!existing?.submitted_at;
  const [fullName, setFullName] = useState(existing?.full_name || "");
  const [addr, setAddr] = useState(existing?.full_address || "");
  const [mobile, setMobile] = useState(existing?.mobile || "");
  // Feb-2026 rev 4:
  // * ``contract_term_years`` — required, 2 or 3. Piped into the
  //   generated contract's wording + end-date calc.
  // * ``franchise_fee_structure`` — required, ``classic`` or
  //   ``flex``. Admin info only at this stage; MUST NOT alter fees,
  //   payments, Flex Payment Choice or any other workflow.
  const [termYears, setTermYears] = useState(
    existing?.contract_term_years === 2 || existing?.contract_term_years === 3
      ? existing.contract_term_years
      : null,
  );
  const [feeStructure, setFeeStructure] = useState(
    existing?.franchise_fee_structure === "classic" || existing?.franchise_fee_structure === "flex"
      ? existing.franchise_fee_structure
      : "",
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const missing = [];
  if (!fullName.trim()) missing.push("Full name");
  if (!addr.trim()) missing.push("Full address");
  if (!mobile.trim()) missing.push("Mobile");
  if (termYears !== 2 && termYears !== 3) missing.push("Contract length");
  if (feeStructure !== "classic" && feeStructure !== "flex") missing.push("Franchise fee structure");
  const canSubmit = !busy && missing.length === 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setErr("");
    try {
      await submitJson(slug, "contract-details", {
        full_name: fullName,
        full_address: addr,
        mobile,
        contract_term_years: termYears,
        franchise_fee_structure: feeStructure,
      });
      onSubmitted && onSubmitted();
    } catch (ex) {
      setErr(ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="launch-section-contract-details">
      <p className="text-stone-700 leading-relaxed">
        Please confirm the personal details we&apos;ll use on your official
        Franchise Agreement, choose your contract length and pick your
        preferred franchise fee structure. Once submitted these details are
        locked and forwarded to Paul or Sandra to prepare your contract.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Full Name" required value={fullName} onChange={setFullName} disabled={submitted} testId="lp-cd-full-name" />
        <Field label="Full Address" required value={addr} onChange={setAddr} disabled={submitted} multiline testId="lp-cd-full-address" />
        <Field label="Mobile" required value={mobile} onChange={setMobile} disabled={submitted} testId="lp-cd-mobile" />

        <FieldWrapper label="How many years would you like your contract to run for?" required testId="lp-cd-term-years">
          <div className="flex gap-4 pt-1" role="radiogroup" aria-label="How many years would you like your contract to run for?">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="contract_term_years"
                value="2"
                checked={termYears === 2}
                disabled={submitted}
                onChange={() => setTermYears(2)}
                data-testid="lp-cd-term-2"
              />
              2 years
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="contract_term_years"
                value="3"
                checked={termYears === 3}
                disabled={submitted}
                onChange={() => setTermYears(3)}
                data-testid="lp-cd-term-3"
              />
              3 years
            </label>
          </div>
        </FieldWrapper>

        <FieldWrapper label="Which franchise fee structure would you like to choose?" required testId="lp-cd-fee-structure">
          <div className="flex gap-4 pt-1" role="radiogroup" aria-label="Which franchise fee structure would you like to choose?">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="franchise_fee_structure"
                value="classic"
                checked={feeStructure === "classic"}
                disabled={submitted}
                onChange={() => setFeeStructure("classic")}
                data-testid="lp-cd-fee-classic"
              />
              CLASSIC
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="franchise_fee_structure"
                value="flex"
                checked={feeStructure === "flex"}
                disabled={submitted}
                onChange={() => setFeeStructure("flex")}
                data-testid="lp-cd-fee-flex"
              />
              FLEX
            </label>
          </div>
        </FieldWrapper>

        {err && <div className="text-sm text-red-700" data-testid="lp-cd-error">{err}</div>}
        {!submitted && (
          <>
            <button
              type="submit"
              disabled={!canSubmit}
              data-testid="lp-cd-submit"
              className="px-5 py-2.5 bg-stone-950 hover:bg-stone-800 text-[#dddd16] rounded-lg text-sm font-bold uppercase tracking-wider disabled:opacity-40"
            >
              {busy ? "Submitting…" : "Submit Contract Details"}
            </button>
            {!canSubmit && missing.length > 0 && (
              <div className="text-xs text-stone-500" data-testid="lp-cd-submit-hint">
                Still needed: {missing.join(", ")}
              </div>
            )}
          </>
        )}
      </form>
      {submitted && <SubmittedLock testId="lp-cd-locked" />}
    </div>
  );
}


// ================================================================
// 6.1c Your Franchise Name
// ----------------------------------------------------------------
// Free-text panel where the franchisee lists their preferred
// franchise name options. First entry = preferred choice. HQ agrees
// the final name offline before launch. Section is immutable once
// submitted (same pattern as Contract Details).
// ================================================================
export function FranchiseNameSection({ slug, launchPortal, onSubmitted }) {
  const existing = launchPortal?.franchise_name;
  const submitted = !!existing?.submitted_at;
  // Show previously-submitted options readback as a single blob so
  // the franchisee can see what they sent — read-only.
  const initialText = submitted
    ? (existing.options || []).join("\n")
    : "";
  const [text, setText] = useState(initialText);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      // Server splits on newlines too, but we do the same locally so
      // the "no options entered" validation can fail fast.
      const options = (text || "")
        .split(/\r?\n/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (options.length === 0) {
        throw new Error("Please enter at least one franchise name option.");
      }
      await submitJson(slug, "franchise-name", { options });
      onSubmitted && onSubmitted();
    } catch (ex) {
      setErr(ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="launch-section-franchise-name">
      <div className="space-y-3 text-stone-700 leading-relaxed">
        <p>
          We now need to decide what your Creative Mojo franchise will be
          called.
        </p>
        <p>
          Your franchise name should make it easy for people to understand
          the area you cover. Typically, this will include one or two of the
          main towns, cities or recognised areas within your territory — for
          example, <em>Creative Mojo Glasgow &amp; Clyde</em> or{" "}
          <em>Creative Mojo Cheshire West &amp; Warrington</em>.
        </p>
        <p>
          Where possible, try to avoid using the name of an entire county on
          its own, as Creative Mojo territories don&apos;t necessarily follow
          county boundaries and your territory may extend into neighbouring
          counties. A more specific geographical reference, such as{" "}
          <em>Creative Mojo North West Devon</em>, can work well because it
          clearly identifies a particular part of the county.
        </p>
        <p>
          Don&apos;t worry about getting it perfect straight away — have a
          think about the places people within your territory are most
          likely to recognise and associate with their local area. We can
          then agree the final name with you before your franchise goes
          live.
        </p>
        <p>
          In the box below, enter as many options as you would like, with the
          first one as your preferred choice. We will then come back to you
          to go over in more detail if your preferred option needs tweaking.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <label
          htmlFor="lp-fn-options"
          className="block text-xs font-semibold uppercase tracking-wider text-stone-600"
        >
          Your franchise name options (one per line — first is preferred)
        </label>
        <textarea
          id="lp-fn-options"
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={submitted || busy}
          rows={6}
          placeholder={
            "Creative Mojo Glasgow & Clyde\nCreative Mojo Greater Glasgow\nCreative Mojo Glasgow North"
          }
          className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-950 disabled:bg-stone-50 disabled:text-stone-500"
          data-testid="lp-fn-options-textarea"
        />
        {err && (
          <div className="text-sm text-red-700" data-testid="lp-fn-error">
            {err}
          </div>
        )}
        {!submitted && (
          <button
            type="submit"
            disabled={busy}
            data-testid="lp-fn-submit"
            className="px-5 py-2.5 bg-stone-950 hover:bg-stone-800 text-[#dddd16] rounded-lg text-sm font-bold uppercase tracking-wider disabled:opacity-40"
          >
            {busy ? "Submitting…" : "Submit Options"}
          </button>
        )}
      </form>

      {submitted && (
        <>
          <SubmittedLock
            testId="lp-fn-locked"
            text={`Options submitted — your preferred choice is ${existing.preferred}.`}
          />
          <div
            className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm text-stone-800"
            data-testid="lp-fn-readback"
          >
            <div className="font-semibold text-stone-900 mb-1">
              Your submitted options
            </div>
            <ol className="list-decimal list-inside space-y-0.5">
              {(existing.options || []).map((opt, i) => (
                <li key={i}>{opt}</li>
              ))}
            </ol>
          </div>
        </>
      )}
    </div>
  );
}


// ================================================================
// 6.1b Flex Payment Option — dedicated Launch Portal section that
// only appears when HQ has toggled ``show_flex_launch`` ON for this
// franchisee. Reuses ``PortalPdfViewer`` (byte-identical to the
// Discovery Portal viewer) and the same
// ``/public/launch-portal/{slug}/flex/{kind}.pdf`` streaming
// endpoints — no new backend, no new viewer.
//
// Below the two PDFs the section also renders a small informational
// "Choose Your Flex Payment Option" selector so the franchisee can
// tell HQ which of the six options they want for their manual Xero
// setup. Editable at any time — no locking, no notifications, no
// Xero/GoCardless integration. This is the ONLY home for the choice
// selector; it deliberately does NOT appear on the Discovery Portal.
// ================================================================
export function FlexPaymentOptionSection({ slug, launchPortal, onRefresh }) {
  const flexStructureInline = `${API}/api/public/launch-portal/${slug}/flex/structure.pdf`;
  const flexStructureDownload = `${API}/api/public/launch-portal/${slug}/flex/structure.pdf/download`;
  const flexAddendumInline = `${API}/api/public/launch-portal/${slug}/flex/addendum.pdf`;
  const flexAddendumDownload = `${API}/api/public/launch-portal/${slug}/flex/addendum.pdf/download`;

  return (
    <div className="space-y-10" data-testid="launch-section-flex">
      <PortalPdfViewer
        title="Flex Payment Structure"
        intro="Our Flex payment structure offers an alternative way to spread the initial cost of starting your Creative Mojo franchise. This overview explains how the payment structure works and the key stages involved."
        inlineUrl={flexStructureInline}
        downloadUrl={flexStructureDownload}
        viewButtonLabel="View Flex Payment Structure"
        downloadButtonLabel="Download a Local Copy"
        modalTitle="Flex Payment Structure"
        testidPrefix="launch-flex-structure"
      />
      <FlexPaymentChoiceSelector
        slug={slug}
        saved={launchPortal?.flex_payment_choice}
        onRefresh={onRefresh}
      />
      <PortalPdfViewer
        title="Flex Addendum Agreement"
        intro="If you choose the Flex payment option, a short addendum is used alongside your main Franchise Agreement to record the additional payment terms. This sample document shows how the Flex arrangement forms part of the overall agreement."
        inlineUrl={flexAddendumInline}
        downloadUrl={flexAddendumDownload}
        viewButtonLabel="View Flex Addendum Agreement"
        downloadButtonLabel="Download a Local Copy"
        modalTitle="Flex Addendum Agreement"
        testidPrefix="launch-flex-addendum"
      />
    </div>
  );
}


// ----------------------------------------------------------------
// FlexPaymentChoiceSelector — six selectable cards + Save button.
// Editable: the franchisee can change and re-save at any time so
// HQ always sees their latest preference. Informational only —
// this is HOW the franchisee tells HQ which option they want for
// their manual Xero invoice setup. No Xero/GoCardless integration,
// no notifications, no completion pill.
// ----------------------------------------------------------------
const FLEX_PAYMENT_OPTION_LIST = [
  { key: "standard",        cadence: "Standard Payment", detail: "£3,500 + VAT initial fee, followed by £165 + VAT per month" },
  { key: "2y_1y_monthly",   cadence: "Monthly",   detail: "£208.33 + VAT per month" },
  { key: "2y_1y_quarterly", cadence: "Quarterly", detail: "£625.00 + VAT per quarter" },
  { key: "2y_2y_monthly",   cadence: "Monthly",   detail: "£104.17 + VAT per month" },
  { key: "2y_2y_quarterly", cadence: "Quarterly", detail: "£312.50 + VAT per quarter" },
  { key: "3y_3y_monthly",   cadence: "Monthly",   detail: "Year 1: £83.33 + VAT/month · Years 2–3: £62.50 + VAT/month" },
  { key: "3y_3y_quarterly", cadence: "Quarterly", detail: "Year 1: £250.00 + VAT/quarter · Years 2–3: £187.50 + VAT/quarter" },
];

function FlexPaymentChoiceSelector({ slug, saved, onRefresh }) {
  const [choice, setChoice] = useState(saved?.key || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => { setChoice(saved?.key || ""); }, [saved?.key]);

  const dirty = choice && choice !== (saved?.key || "");

  const handleSave = async () => {
    if (!choice) { setErr("Please pick one option first."); return; }
    setBusy(true); setErr(null); setJustSaved(false);
    try {
      await submitJson(slug, "flex-payment-choice", { key: choice });
      setJustSaved(true);
      onRefresh && (await onRefresh());
    } catch (e) {
      setErr(e.message || "Could not save your choice. Please try again.");
    } finally { setBusy(false); }
  };

  const groups = [
    { title: "Standard Payment Option", dividerAfter: true, subs: [
      { subtitle: "Main payment structure", keys: ["standard"] },
    ]},
    { title: "Flex Payment Option — 2 Year Franchise Agreement", subs: [
      { subtitle: "Repay Flex over 1 year",  keys: ["2y_1y_monthly", "2y_1y_quarterly"] },
      { subtitle: "Repay Flex over 2 years", keys: ["2y_2y_monthly", "2y_2y_quarterly"] },
    ]},
    { title: "Flex Payment Option — 3 Year Franchise Agreement", subs: [
      { subtitle: "Repay Flex over 3 years", keys: ["3y_3y_monthly", "3y_3y_quarterly"] },
    ]},
  ];

  return (
    <div className="mt-2" data-testid="launch-flex-choice">
      <h3 className="text-2xl md:text-3xl font-black text-stone-950 mb-2">
        Choose Your Payment Option
      </h3>
      <p className="text-stone-700 leading-relaxed mb-6 max-w-3xl">
        You can pay for your Creative Mojo franchise using our Standard payment
        (an initial fee followed by monthly payments) or spread the initial
        cost using one of our Mojo Flex options. Please select your preferred
        option below so we know which to set up — you can change your
        selection later if you need to.
      </p>

      {saved && (
        <div
          className="mb-4 rounded-lg border border-stone-300 bg-stone-50 text-stone-800 text-sm p-3"
          data-testid="launch-flex-choice-current"
        >
          <div className="font-bold text-stone-900">Your current choice</div>
          <div className="mt-0.5">
            {saved.agreement_label} — {saved.repay_label} — {saved.cadence_label}
          </div>
          <div className="text-[11px] text-stone-600 mt-0.5">
            {saved.detail_label}
          </div>
          {saved.saved_at && (
            <div className="text-[10px] text-stone-500 mt-1">
              Last saved {new Date(saved.saved_at).toLocaleString("en-GB")}
            </div>
          )}
        </div>
      )}

      <div className="space-y-6">
        {groups.map((g, gi) => (
          <React.Fragment key={g.title}>
            <div>
              <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500 mb-2">
                {g.title}
              </div>
              {g.subs.map((sub) => (
                <div key={sub.subtitle} className="mb-3">
                  <div className="text-sm font-bold text-stone-900 mb-2">{sub.subtitle}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {sub.keys.map((k) => {
                      const opt = FLEX_PAYMENT_OPTION_LIST.find((o) => o.key === k);
                      const active = choice === k;
                      return (
                        <button
                          key={k}
                          type="button"
                          onClick={() => setChoice(k)}
                          data-testid={`launch-flex-choice-option-${k}`}
                          className={
                            "flex items-start gap-3 text-left rounded-xl border-2 p-4 transition-colors " +
                            (active
                              ? "border-stone-900 bg-stone-50"
                              : "border-stone-200 bg-white hover:border-stone-400")
                          }
                        >
                          <span
                            aria-hidden="true"
                            className={
                              "mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 " +
                              (active ? "border-stone-900 bg-stone-900" : "border-stone-400 bg-white")
                            }
                          >
                            {active && <span className="w-2 h-2 rounded-full bg-white" />}
                          </span>
                          <span className="min-w-0">
                            <span className="block text-sm font-bold text-stone-900">
                              {opt.cadence}
                            </span>
                            <span className="block text-sm text-stone-700 mt-0.5">
                              {opt.detail}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {g.dividerAfter && gi < groups.length - 1 && (
              <div className="relative py-3" data-testid="launch-payment-options-divider">
                <div className="absolute inset-x-0 top-1/2 h-0.5 bg-stone-300" />
                <div className="relative flex justify-center">
                  <span className="bg-stone-50 px-3 text-[11px] font-bold uppercase tracking-widest text-stone-500">Or</span>
                </div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {err && (
        <div className="mt-4 text-sm text-red-700" data-testid="launch-flex-choice-error">{err}</div>
      )}

      {justSaved && !dirty && (
        <div className="mt-4 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-900 text-sm p-3" data-testid="launch-flex-choice-saved">
          ✓ Your preferred payment option has been saved.
        </div>
      )}

      <button
        type="button"
        onClick={handleSave}
        disabled={busy || !choice || !dirty}
        data-testid="launch-flex-choice-save"
        className={
          "mt-5 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold uppercase tracking-wider rounded-lg " +
          (busy || !choice || !dirty
            ? "bg-stone-200 text-stone-500 cursor-not-allowed"
            : "bg-stone-900 hover:bg-stone-800 text-white")
        }
      >
        {busy ? "Saving…" : (saved ? "Update My Payment Choice" : "Save My Payment Choice")}
      </button>
    </div>
  );
}


// ================================================================
// 6.2 Territory Preparation — reuses the Discovery territory + adds
// a CONFIRM TERRITORY button.
// ================================================================
export function LaunchTerritoryConfirmationSection({ slug, launchPortal, onSubmitted }) {
  const existing = launchPortal.territory_confirmation;
  const confirmed = !!existing?.confirmed;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const confirm = async () => {
    if (!window.confirm("Confirm your territory? This locks in the boundary shown in your Discovery Portal above.")) return;
    setBusy(true); setErr("");
    try {
      await submitJson(slug, "territory-confirmation", {});
      onSubmitted && onSubmitted();
    } catch (ex) {
      setErr(ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="launch-section-territory-confirmation">
      <p className="text-stone-700 leading-relaxed">
        Your proposed territory (shown in the Discovery Portal navigation above) has
        been finalised by HQ. Click below to confirm the boundary you&apos;ve reviewed and
        formally lock it in as your Creative Mojo trading area.
      </p>
      {confirmed ? (
        <SubmittedLock testId="lp-territory-locked" text={`Confirmed on ${formatDate(existing.confirmation_date)}.`} />
      ) : (
        <>
          {err && <div className="text-sm text-red-700" data-testid="lp-territory-error">{err}</div>}
          <button
            onClick={confirm}
            disabled={busy}
            data-testid="lp-territory-confirm"
            className="px-5 py-2.5 bg-stone-950 hover:bg-stone-800 text-[#dddd16] rounded-lg text-sm font-bold uppercase tracking-wider disabled:opacity-40"
          >
            {busy ? "Confirming…" : "Confirm Territory"}
          </button>
        </>
      )}
    </div>
  );
}


// ================================================================
// 6.3 Email Account
// ================================================================
const EMAIL_DOMAIN = "@creativemojo.co.uk";

export function EmailAccountSection({ slug, launchPortal, onSubmitted }) {
  const emailBlock = launchPortal.email || {};
  const reserved = emailBlock.reserved_address;
  const creds = emailBlock.credentials || null;

  // Franchisees type only the mailbox name before the @ — Sam's Feb
  // 2026 UI fix. The ``@creativemojo.co.uk`` suffix is a fixed,
  // non-editable visual segment inside the same input control. The
  // wire format (backend contract) is unchanged: availability check
  // and reservation both send the fully-constructed address.
  const [localPart, setLocalPart] = useState("");
  const [availability, setAvailability] = useState(null); // null | {available, reason?}
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Guard against paste-in of "sara.smith@creativemojo.co.uk" by
  // stripping anything from the @ onwards; also lowercase and drop
  // characters that would fail the server's local-part validator.
  const handleLocalChange = (raw) => {
    const cleaned = String(raw || "")
      .toLowerCase()
      .split("@", 1)[0]
      .replace(/[^a-z0-9._-]/g, "");
    setLocalPart(cleaned);
    setAvailability(null);
  };

  const fullAddress = localPart ? `${localPart}${EMAIL_DOMAIN}` : "";

  const check = async () => {
    setChecking(true); setErr("");
    try {
      const q = new URLSearchParams({ address: fullAddress }).toString();
      const res = await fetch(`${API}/api/public/launch-portal/${slug}/email-availability?${q}`, {
        credentials: "include",
      });
      const data = await res.json();
      setAvailability(data);
    } catch (ex) { setErr("Availability check failed."); }
    finally { setChecking(false); }
  };

  const reserve = async () => {
    setBusy(true); setErr("");
    try {
      await submitJson(slug, "email-account", { email_address: fullAddress });
      onSubmitted && onSubmitted();
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="launch-section-email-account">
      {emailBlock.guidance_text && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="lp-email-guidance">
          {emailBlock.guidance_text}
        </div>
      )}
      {!reserved && (
        <>
          <p className="text-stone-700 leading-relaxed">
            Choose your personalised <b>@creativemojo.co.uk</b> email address. Only
            lowercase letters, digits, dot, underscore or dash are allowed before
            the @.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <div
              className="flex-1 min-w-[280px] flex items-stretch border border-stone-300 rounded-lg bg-white overflow-hidden focus-within:ring-2 focus-within:ring-stone-950/20"
              data-testid="lp-email-input-group"
            >
              <input
                type="text"
                inputMode="email"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                value={localPart}
                onChange={(e) => handleLocalChange(e.target.value)}
                placeholder="e.g. sara.smith"
                data-testid="lp-email-input"
                className="flex-1 min-w-0 px-3 py-2 text-sm bg-transparent focus:outline-none"
              />
              <span
                className="px-3 py-2 text-sm text-stone-600 bg-stone-50 border-l border-stone-200 select-none whitespace-nowrap"
                data-testid="lp-email-domain-suffix"
                aria-hidden="true"
              >
                {EMAIL_DOMAIN}
              </span>
            </div>
            <button
              onClick={check}
              disabled={!localPart || checking}
              data-testid="lp-email-check"
              className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-stone-950 bg-white hover:bg-stone-50 rounded-lg disabled:opacity-40"
            >
              {checking ? "Checking…" : "Check availability"}
            </button>
          </div>
          {availability && (
            <div
              className={
                "flex items-center gap-2 text-sm " +
                (availability.available ? "text-emerald-800" : "text-red-700")
              }
              data-testid="lp-email-availability"
            >
              {availability.available ? <MailCheck className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {availability.available ? `${fullAddress} is available — you can reserve this address.` : (availability.reason || "Not available")}
            </div>
          )}
          {err && <div className="text-sm text-red-700" data-testid="lp-email-error">{err}</div>}
          {availability?.available && (
            <button
              onClick={reserve}
              disabled={busy}
              data-testid="lp-email-reserve"
              className="px-5 py-2.5 bg-stone-950 hover:bg-stone-800 text-[#dddd16] rounded-lg text-sm font-bold uppercase tracking-wider disabled:opacity-40"
            >
              {busy ? "Reserving…" : "Reserve this address"}
            </button>
          )}
        </>
      )}
      {reserved && (
        <>
          <SubmittedLock text={`You've reserved ${reserved}.`} testId="lp-email-reserved-lock" />
          {creds?.password ? (
            <div className="rounded-xl border border-stone-200 bg-white p-4 space-y-2 text-sm" data-testid="lp-email-credentials">
              <div className="font-bold text-stone-900">Your mailbox is ready.</div>
              <KV label="Email address" value={creds.email_address} />
              <KV label="Username" value={creds.username} />
              <KV label="Password" value={creds.password} mono />
              <hr className="my-2" />
              {creds.imap_host && <KV label="IMAP server" value={creds.imap_host} mono />}
              {creds.imap_host && creds.imap_port && <KV label="Port" value={String(creds.imap_port)} mono />}
              {creds.smtp_host && <KV label="SMTP server" value={creds.smtp_host} mono />}
              {creds.smtp_host && creds.smtp_port && <KV label="Port" value={String(creds.smtp_port)} mono />}
              {creds.webmail_url && (
                <div>
                  <a href={creds.webmail_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-700 font-bold hover:underline">
                    Open Webmail <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              )}
              {(creds.setup_video_desktop || creds.setup_video_mobile) && (
                <div className="pt-2">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Setup guides</div>
                  <div className="flex flex-wrap gap-2">
                    {creds.setup_video_desktop && (
                      <a href={creds.setup_video_desktop} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 underline">Desktop setup</a>
                    )}
                    {creds.setup_video_mobile && (
                      <a href={creds.setup_video_mobile} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 underline">Mobile setup</a>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-stone-600 italic" data-testid="lp-email-waiting-hq">
              HQ is finalising your mailbox credentials — you&apos;ll see the login details here as soon as they&apos;re ready.
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ================================================================
// 6.4 Personalised Facebook Setup
// ================================================================
export function FacebookSetupSection({ slug, launchPortal, onSubmitted }) {
  const fb = launchPortal.facebook || {};
  const submission = launchPortal.facebook_submission;
  const submitted = !!submission?.submitted_at;
  const [fbEmail, setFbEmail] = useState(submission?.facebook_login_email || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await submitJson(slug, "facebook", { facebook_login_email: fbEmail });
      onSubmitted && onSubmitted();
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="launch-section-facebook">
      <p className="text-stone-700 leading-relaxed">
        We&apos;ll be setting up a personalised Creative Mojo Facebook Page for you.
        To do this, please share the email address you use to log into Facebook.
      </p>
      <p className="text-stone-700 leading-relaxed">
        Once we have this, we will request for you to join our Creative Mojo
        Facebook Business. Please can you check your email for an email invite
        from Meta and follow the instructions to accept the invite. If you
        don&apos;t see any emails in your in-box, please check your spam folder.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Your Facebook Login Email" value={fbEmail} onChange={setFbEmail} disabled={submitted} testId="lp-fb-login-email" />
        {err && <div className="text-sm text-red-700" data-testid="lp-fb-error">{err}</div>}
        {!submitted && (
          <button
            type="submit"
            disabled={busy}
            data-testid="lp-fb-submit"
            className="px-5 py-2.5 bg-stone-950 hover:bg-stone-800 text-[#dddd16] rounded-lg text-sm font-bold uppercase tracking-wider disabled:opacity-40"
          >
            {busy ? "Submitting…" : "Submit Facebook Email"}
          </button>
        )}
      </form>
      {submitted && (
        <div className="space-y-2">
          <SubmittedLock text={`Submitted ${formatDate(submission.submitted_at)}.`} testId="lp-fb-locked" />
          {fb.invitation_link && (
            <a
              href={fb.invitation_link}
              target="_blank"
              rel="noreferrer"
              data-testid="lp-fb-invitation"
              className="inline-flex items-center gap-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold uppercase tracking-wider rounded-lg"
            >
              Accept Meta invitation <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          {fb.creative_mojo_page_url && (
            <a
              href={fb.creative_mojo_page_url}
              target="_blank"
              rel="noreferrer"
              data-testid="lp-fb-cm-page"
              className="inline-flex items-center gap-1 px-4 py-2 border border-stone-950 bg-white hover:bg-stone-50 text-sm font-bold uppercase tracking-wider rounded-lg ml-0 md:ml-2"
            >
              Visit Creative Mojo Facebook <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}


// ================================================================
// 6.5 Website Listing — informational only
// ================================================================
export function WebsiteListingSection() {
  return (
    <div className="space-y-3 max-w-2xl" data-testid="launch-section-website-listing">
      <p className="text-stone-700 leading-relaxed">
        Once your Franchise Agreement is signed, HQ will add you to the Creative
        Mojo website map system so prospective clients can find you in your
        territory. No action is required from you at this stage — we&apos;ll notify
        you once your public listing goes live.
      </p>
    </div>
  );
}


// ================================================================
// 6.6 DBS Application — full form + up to 3 R2 uploads
// ------------------------------------------------------------------
// Required-field rules (Feb-2026 brief):
//   * Date of birth, Birth surname, and Birth surname used until
//     are all required.
//   * "Birth surname used until" accepts either a month/year OR
//     the sentinel "still-current" (rendered as "Still used/current").
//   * Address history must cover the last 5 years continuously —
//     no gaps, no future dates, from ≤ to.  Any gap is displayed
//     inline and prevents submit.
//   * On reopen (HQ has cleared submitted_at), prefill from the
//     preserved payload so no re-typing is needed. Existing file
//     uploads are preserved unless the applicant replaces them.
// ================================================================

// ---- MonthYearPicker: two dropdowns, optional "Present" sentinel.
// ``presentValue`` (if provided) is what will be emitted when the
// user selects the "Present"/"Still used/current" option — this lets
// the same picker back both address-history "to" (present) and the
// birth-surname-used-until field (still-current).
const _MONTHS = [
  ["01","Jan"],["02","Feb"],["03","Mar"],["04","Apr"],["05","May"],["06","Jun"],
  ["07","Jul"],["08","Aug"],["09","Sep"],["10","Oct"],["11","Nov"],["12","Dec"],
];
const _CURRENT_YEAR = new Date().getFullYear();
const _YEARS = Array.from({ length: 66 }, (_, i) => String(_CURRENT_YEAR + 5 - i));

// ---- YearPicker: single YYYY dropdown. Used for the DBS
// "This name was used until" field (matches the paper DBS form).
// Selecting the CURRENT year is the sentinel for "still used".
// Accepts legacy values on prefill: YYYY-MM, YYYY-MM-DD, or the
// literal "still-current" — all reduce to a single 4-digit year.
function _coerceYearFromLegacy(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return "";
  if (s === "still-current") return String(new Date().getFullYear());
  const m = /^(\d{4})/.exec(s);
  return m ? m[1] : "";
}
function YearPicker({ value, onChange, disabled, testId, minYear = 1900 }) {
  const y = /^\d{4}$/.test(String(value || "")) ? String(value) : _coerceYearFromLegacy(value);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: (currentYear - minYear) + 1 }, (_, i) => String(currentYear - i));
  const cls = "w-full px-3 py-2 text-sm border border-stone-300 rounded-lg bg-white focus:outline-none focus:border-stone-900 disabled:bg-stone-50 disabled:text-stone-500";
  return (
    <select
      disabled={disabled}
      value={y}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className={cls}
    >
      <option value="">Select year</option>
      {years.map((yy) => <option key={yy} value={yy}>{yy}</option>)}
    </select>
  );
}

function MonthYearPicker({ value, onChange, presentValue = null, presentLabel = "Present", disabled, testId }) {
  const isPresent = presentValue && value === presentValue;
  // Accept legacy YYYY-MM-DD by trimming to YYYY-MM for the pickers.
  const ym = isPresent ? "" : (value || "").slice(0, 7);
  const [y = "", m = ""] = ym.split("-");
  const emit = (nextY, nextM) => {
    if (nextY === "__present__") { onChange(presentValue); return; }
    if (!nextY && !nextM) { onChange(""); return; }
    if (nextY && nextM) { onChange(`${nextY}-${nextM}`); return; }
    onChange(`${nextY || ""}${nextM ? `-${nextM}` : ""}`);
  };
  const inputCls = "w-full px-2 py-2 text-sm border border-stone-300 rounded-lg bg-white focus:outline-none focus:border-stone-900 disabled:bg-stone-50 disabled:text-stone-500";
  return (
    <div className="flex gap-1.5">
      <select
        disabled={disabled || isPresent}
        value={isPresent ? "" : m}
        onChange={(e) => emit(y, e.target.value)}
        data-testid={testId ? `${testId}-month` : undefined}
        className={inputCls + " flex-1"}
      >
        <option value="">Month</option>
        {_MONTHS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <select
        disabled={disabled}
        value={isPresent ? "__present__" : y}
        onChange={(e) => emit(e.target.value, m)}
        data-testid={testId ? `${testId}-year` : undefined}
        className={inputCls + " flex-1"}
      >
        <option value="">Year</option>
        {presentValue && <option value="__present__">{presentLabel}</option>}
        {_YEARS.map((yy) => <option key={yy} value={yy}>{yy}</option>)}
      </select>
    </div>
  );
}

// ---- Shared FE/BE coverage validator. Mirrors validate_dbs_address_history
// in backend/launch_portal_public_routes.py. Returns { errors, gaps }.
function _parseYm(raw) {
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec((raw || "").trim());
  if (!m) return null;
  return { y: parseInt(m[1], 10), mo: parseInt(m[2], 10) };
}
function _ymOrd(y, mo) { return y * 12 + mo; }
function _ordLabel(o) {
  let y = Math.floor((o - 1) / 12);
  let mo = ((o - 1) % 12) + 1;
  return `${_MONTHS[mo - 1][1]} ${y}`;
}
function validateAddressCoverage(rows, today = new Date()) {
  const errors = [];
  const gaps = [];
  if (!rows || rows.length === 0) {
    return { errors: ["Please add at least one address."], gaps: [] };
  }
  const todayOrd = _ymOrd(today.getFullYear(), today.getMonth() + 1);
  const fiveYearsAgoOrd = todayOrd - 60;
  const parsed = [];
  rows.forEach((row, i) => {
    const label = `Address ${i + 1}`;
    const hasStructured = !!(
      (row?.house_name_number || "").trim() &&
      (row?.street || "").trim() &&
      (row?.town_city || "").trim() &&
      (row?.county || "").trim() &&
      (row?.postcode || "").trim()
    );
    const hasLegacy = !!(row?.full_address || row?.address || "").trim();
    if (!hasStructured && !hasLegacy) {
      errors.push(`${label}: house name/number, street, town/city, county and postcode are required.`);
    }
    const fYm = _parseYm(row?.from);
    if (!fYm) errors.push(`${label}: 'lived there from' month and year are required.`);
    let tOrd = null;
    let tOk = true;
    const toRaw = (row?.to || "").trim().toLowerCase();
    if (toRaw === "present") {
      tOrd = todayOrd;
    } else {
      const tYm = _parseYm(row?.to);
      if (!tYm) { errors.push(`${label}: 'lived there to' month and year (or 'Present') are required.`); tOk = false; }
      else tOrd = _ymOrd(tYm.y, tYm.mo);
    }
    if (fYm && tOk && tOrd !== null) {
      const fOrd = _ymOrd(fYm.y, fYm.mo);
      if (fOrd > todayOrd) errors.push(`${label}: 'from' date cannot be in the future.`);
      if (tOrd > todayOrd) errors.push(`${label}: 'to' date cannot be in the future.`);
      if (fOrd > tOrd) errors.push(`${label}: 'from' cannot be later than 'to'.`);
      if (fOrd <= tOrd && fOrd <= todayOrd && tOrd <= todayOrd) {
        parsed.push([fOrd, tOrd]);
      }
    }
  });
  if (errors.length) return { errors, gaps: [] };

  parsed.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const merged = [];
  for (const [a, b] of parsed) {
    if (merged.length && a <= merged[merged.length - 1][1] + 1) {
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], b);
    } else {
      merged.push([a, b]);
    }
  }
  let cursor = fiveYearsAgoOrd;
  for (const [a, b] of merged) {
    if (b < cursor) continue;
    if (a > cursor) gaps.push([cursor, Math.min(a - 1, todayOrd)]);
    cursor = Math.max(cursor, b + 1);
    if (cursor > todayOrd) break;
  }
  if (cursor <= todayOrd) gaps.push([cursor, todayOrd]);
  return { errors: [], gaps };
}

// ---- Prefill helpers for reopen state.
function _rehydrateForm(prefill) {
  const p = prefill || {};
  const asYesNo = (v) => {
    if (v === true || v === "yes") return "yes";
    if (v === false || v === "no") return "no";
    return "";
  };
  return {
    forename: p.forename || "",
    middle_names: p.middle_names || "",
    surname: p.surname || "",
    date_of_birth: p.date_of_birth || "",
    gender: p.gender || "",
    place_of_birth_town: p.place_of_birth_town || "",
    place_of_birth_county: p.place_of_birth_county || "",
    place_of_birth_country: p.place_of_birth_country || "",
    nationality_at_birth: p.nationality_at_birth || "",
    changed_nationality: asYesNo(p.changed_nationality),
    current_nationality: p.current_nationality || "",
    birth_surname: p.birth_surname || "",
    birth_surname_used_until: _coerceYearFromLegacy(p.birth_surname_used_until),
    has_other_names: asYesNo(p.has_other_names),
    has_convictions: asYesNo(p.has_convictions),
    conviction_details: p.conviction_details || "",
    ni_number: p.ni_number || "",
    doc1_type: (p.doc1 && p.doc1.type) || "",
    doc2_type: (p.doc2 && p.doc2.type) || "",
    doc3_type: (p.doc3 && p.doc3.type) || "",
  };
}
function _rehydrateOtherNames(prefill) {
  const rows = (prefill && Array.isArray(prefill.other_names) && prefill.other_names) || [];
  return rows.map((r) => ({
    name: r?.name || "",
    type: r?.type || "",
    from_year: r?.from_year || "",
    to_year: r?.to_year || "",
  }));
}
function _emptyAddrRow() {
  return {
    __legacy: false,
    full_address: "",
    house_name_number: "",
    street: "",
    town_city: "",
    county: "",
    postcode: "",
    from: "",
    to: "",
  };
}
function _rehydrateAddrHist(prefill) {
  const rows = (prefill && Array.isArray(prefill.address_history) && prefill.address_history) || [];
  if (!rows.length) return [_emptyAddrRow()];
  return rows.map((r) => {
    // Legacy row = free-text ``full_address`` (or older ``address``)
    // but no structured fields at all. Kept unaltered per HQ policy.
    const legacyText = r?.full_address || r?.address || "";
    const hasStruct = !!(
      r?.house_name_number || r?.street || r?.town_city || r?.county || r?.postcode
    );
    return {
      __legacy: !hasStruct && !!legacyText,
      full_address: legacyText,
      house_name_number: r?.house_name_number || "",
      street: r?.street || "",
      town_city: r?.town_city || "",
      county: r?.county || "",
      postcode: r?.postcode || "",
      from: r?.from || "",
      to: r?.to || "",
    };
  });
}

export function DBSSection({ slug, launchPortal, docTypes, onSubmitted }) {
  const submittedAt = launchPortal.dbs_submitted_at;
  const submitted = !!submittedAt;
  const prefill = launchPortal.dbs_prefill || null;
  const isReopened = !submitted && !!prefill;
  // A NEW application (no prefill) requires all three fresh uploads.
  // A REOPENED application only requires an upload for a slot that
  // has no preserved document on file.
  const isNewApplication = !isReopened;

  const [form, setForm] = useState(() => _rehydrateForm(prefill));
  const [addrHist, setAddrHist] = useState(() => _rehydrateAddrHist(prefill));
  const [otherNames, setOtherNames] = useState(() => _rehydrateOtherNames(prefill));
  const [files, setFiles] = useState({ doc1: null, doc2: null, doc3: null });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const existingUploads = {
    doc1: (prefill && prefill.doc1) || null,
    doc2: (prefill && prefill.doc2) || null,
    doc3: (prefill && prefill.doc3) || null,
  };

  // Unsaved-progress guard — Feb-2026 (Sam). Every user-driven state
  // mutator funnels through ``touch()`` which flips the gate dirty
  // the first time it fires. ``markClean()`` is called on successful
  // submit and on unmount (so navigating away never leaves the gate
  // stuck dirty on behalf of a no-longer-mounted form).
  const { markDirty, markClean } = useDbsDirtyGate();
  const touch = useCallback(() => markDirty(), [markDirty]);
  useEffect(() => () => markClean(), [markClean]);

  const setF = (k) => (v) => { touch(); setForm((s) => ({ ...s, [k]: v })); };
  const addHist = () => { touch(); setAddrHist((h) => [...h, _emptyAddrRow()]); };
  const removeHist = (i) => { touch(); setAddrHist((h) => h.filter((_, idx) => idx !== i)); };
  const setHist = (i, k, v) => { touch(); setAddrHist((h) => h.map((row, idx) => idx === i ? { ...row, [k]: v } : row)); };
  const addOtherName = () => { touch(); setOtherNames((rows) => [...rows, { name: "", type: "", from_year: "", to_year: "" }]); };
  const removeOtherName = (i) => { touch(); setOtherNames((rows) => rows.filter((_, idx) => idx !== i)); };
  const setOtherName = (i, k, v) => { touch(); setOtherNames((rows) => rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r)); };

  // ---- Live validation summary (used to disable Submit + render errors)
  const coverage = validateAddressCoverage(addrHist);
  const missingPersonal = [];
  if (!form.forename.trim()) missingPersonal.push("Forename");
  if (!form.surname.trim()) missingPersonal.push("Surname");
  if (!form.date_of_birth) missingPersonal.push("Date of birth");
  if (!form.gender) missingPersonal.push("Gender");
  if (!form.place_of_birth_town.trim()) missingPersonal.push("Town of birth");
  if (!form.place_of_birth_country.trim()) missingPersonal.push("Country of birth");
  if (!form.nationality_at_birth.trim()) missingPersonal.push("Nationality at birth");
  if (!form.changed_nationality) missingPersonal.push("Changed nationality Yes/No");
  if (form.changed_nationality === "yes" && !form.current_nationality.trim()) missingPersonal.push("Current nationality");
  if (!form.birth_surname.trim()) missingPersonal.push("Birth surname");
  if (!form.birth_surname_used_until) missingPersonal.push("Birth surname used until");
  if (!form.has_other_names) missingPersonal.push("Other names Yes/No");
  if (!form.ni_number.trim()) missingPersonal.push("National Insurance Number");
  if (!form.doc1_type) missingPersonal.push("Document 1 type");
  if (!form.doc2_type) missingPersonal.push("Document 2 type");
  if (!form.doc3_type) missingPersonal.push("Document 3 type");
  if (!form.has_convictions) missingPersonal.push("Convictions Yes/No");
  if (form.has_convictions === "yes" && !form.conviction_details.trim()) missingPersonal.push("Conviction details");

  // ---- Other Names live validation
  const otherNameErrors = [];
  if (form.has_other_names === "yes") {
    if (otherNames.length === 0) {
      otherNameErrors.push("Please add at least one other-name entry.");
    } else {
      const cy = new Date().getFullYear();
      otherNames.forEach((r, i) => {
        const label = `Other name ${i + 1}`;
        if (!r.name.trim()) otherNameErrors.push(`${label}: name is required.`);
        if (r.type !== "Forename" && r.type !== "Surname") otherNameErrors.push(`${label}: type must be Forename or Surname.`);
        const fyOk = /^\d{4}$/.test(r.from_year) && +r.from_year >= 1900 && +r.from_year <= cy;
        const tyOk = /^\d{4}$/.test(r.to_year) && +r.to_year >= 1900 && +r.to_year <= cy;
        if (!fyOk) otherNameErrors.push(`${label}: from year is required (YYYY, up to ${cy}).`);
        if (!tyOk) otherNameErrors.push(`${label}: to year is required (YYYY, up to ${cy}).`);
        if (fyOk && tyOk && +r.from_year > +r.to_year) otherNameErrors.push(`${label}: from year cannot be later than to year.`);
      });
    }
  }
  // Files: on a new application every slot needs a fresh upload; on a
  // reopen a slot only needs a fresh upload if there's nothing on file
  // for it.
  const filesMissing = [];
  if (!files.doc1 && (isNewApplication || !existingUploads.doc1)) filesMissing.push("Document 1 file");
  if (!files.doc2 && (isNewApplication || !existingUploads.doc2)) filesMissing.push("Document 2 file");
  if (!files.doc3 && (isNewApplication || !existingUploads.doc3)) filesMissing.push("Document 3 file");

  const gapMessages = coverage.gaps.map(([a, b]) => `${_ordLabel(a)} – ${_ordLabel(b)}`);
  const canSubmit = !busy
    && missingPersonal.length === 0
    && filesMissing.length === 0
    && coverage.errors.length === 0
    && coverage.gaps.length === 0
    && otherNameErrors.length === 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("payload_json", JSON.stringify({
        ...form,
        address_history: addrHist,
        other_names: form.has_other_names === "yes" ? otherNames : [],
      }));
      if (files.doc1) fd.append("doc1", files.doc1);
      if (files.doc2) fd.append("doc2", files.doc2);
      if (files.doc3) fd.append("doc3", files.doc3);
      await submitMultipart(slug, "dbs", fd);
      // Clear the unsaved-progress guard BEFORE the parent reload so
      // the subsequent re-render (which may unmount this section) no
      // longer trips the warning. A failed submit falls through the
      // ``catch`` below and deliberately leaves the gate dirty so the
      // user is still warned.
      markClean();
      onSubmitted && onSubmitted();
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div data-testid="launch-section-dbs">
        <p className="text-stone-700 leading-relaxed max-w-2xl">
          Your DBS application has been submitted to HQ. Paul or Sandra will
          process it and be in touch if anything else is needed.
        </p>
        <SubmittedLock text={`Submitted ${formatDate(submittedAt)} — this form is now locked.`} testId="lp-dbs-locked" />
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-6 max-w-3xl" data-testid="launch-section-dbs">
      {isReopened && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-sm" data-testid="lp-dbs-reopened-banner">
          Your DBS application has been reopened by HQ so you can complete a few extra details.
          Your previous entries and uploaded documents are still on file — just review, add the
          missing information, and resubmit.
        </div>
      )}
      <p className="text-stone-700 leading-relaxed">
        Please complete your Disclosure &amp; Barring Service (DBS) application.
        Fields marked with <span className="text-red-600">*</span> are required.
        Once submitted, this form is <b>locked</b> and cannot be edited — please
        double-check your details before submitting.
      </p>

      <FieldGroup title="Personal details">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Forename(s)" required value={form.forename} onChange={setF("forename")} testId="lp-dbs-forename" />
          <Field label="Middle name(s)" value={form.middle_names} onChange={setF("middle_names")} testId="lp-dbs-middle" />
          <Field label="Surname" required value={form.surname} onChange={setF("surname")} testId="lp-dbs-surname" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Date of birth" required type="date" value={form.date_of_birth} onChange={setF("date_of_birth")} testId="lp-dbs-dob" />
          <FieldWrapper label="Gender" required testId="lp-dbs-gender">
            <select
              value={form.gender || ""}
              onChange={(e) => setF("gender")(e.target.value)}
              data-testid="lp-dbs-gender-select"
              className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg bg-white"
            >
              <option value="">Select</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </FieldWrapper>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Surname at birth" required value={form.birth_surname} onChange={setF("birth_surname")} testId="lp-dbs-birthsurname" />
          <FieldWrapper label="This name was used until (year)" required testId="lp-dbs-birthsurname-used-until">
            <YearPicker
              value={form.birth_surname_used_until}
              onChange={setF("birth_surname_used_until")}
              testId="lp-dbs-birthsurname-used-until"
            />
            <p className="text-[10px] text-stone-500 mt-1">
              If the surname is still used, select the current year.
            </p>
          </FieldWrapper>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Town of birth" required value={form.place_of_birth_town} onChange={setF("place_of_birth_town")} testId="lp-dbs-birthtown" />
          <Field label="County of birth" value={form.place_of_birth_county} onChange={setF("place_of_birth_county")} testId="lp-dbs-birthcounty" />
          <Field label="Country of birth" required value={form.place_of_birth_country} onChange={setF("place_of_birth_country")} testId="lp-dbs-birthcountry" />
        </div>
        <Field label="National Insurance Number" required value={form.ni_number} onChange={setF("ni_number")} testId="lp-dbs-ni" />
      </FieldGroup>

      <FieldGroup title="Nationality">
        <Field label="Nationality at birth" required value={form.nationality_at_birth} onChange={setF("nationality_at_birth")} testId="lp-dbs-nationality-birth" />
        <FieldWrapper label="Have you changed your nationality since birth?" required testId="lp-dbs-changed-nationality">
          <div className="flex gap-4 pt-1" role="radiogroup" aria-label="Have you changed your nationality since birth?">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="changed_nationality"
                value="yes"
                checked={form.changed_nationality === "yes"}
                onChange={() => setF("changed_nationality")("yes")}
                data-testid="lp-dbs-changed-nationality-yes"
              />
              Yes
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="changed_nationality"
                value="no"
                checked={form.changed_nationality === "no"}
                onChange={() => { setF("changed_nationality")("no"); setF("current_nationality")(""); }}
                data-testid="lp-dbs-changed-nationality-no"
              />
              No
            </label>
          </div>
        </FieldWrapper>
        {form.changed_nationality === "yes" && (
          <Field label="Current nationality" required value={form.current_nationality} onChange={setF("current_nationality")} testId="lp-dbs-current-nationality" />
        )}
      </FieldGroup>

      <FieldGroup title="Address history (last 5 years)">
        <p className="text-xs text-stone-500 mb-2">
          Add every address you&apos;ve lived at during the last 5 years. Cover the
          full period with no gaps.
        </p>
        <div className="space-y-3">
          {addrHist.map((row, i) => (
            <div key={i} className="border border-stone-200 rounded-lg p-3 space-y-2" data-testid={`lp-dbs-addr-row-${i}`}>
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Address {i + 1}</div>
                {addrHist.length > 1 && (
                  <button type="button" onClick={() => removeHist(i)} data-testid={`lp-dbs-hist-remove-${i}`} className="text-xs text-red-700 underline">Remove</button>
                )}
              </div>
              {row.__legacy ? (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 space-y-1" data-testid={`lp-dbs-addr-legacy-${i}`}>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-amber-900">Existing address on file</div>
                  <div className="text-sm text-stone-900 whitespace-pre-wrap">{row.full_address}</div>
                  <div className="text-[10px] text-amber-900 italic">Preserved from your earlier submission — HQ can still see the full text. Remove and re-add if you'd like to enter it in the new structured form.</div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="House name/number" required value={row.house_name_number} onChange={(v) => setHist(i, "house_name_number", v)} testId={`lp-dbs-hist-house-${i}`} />
                  <Field label="Street" required value={row.street} onChange={(v) => setHist(i, "street", v)} testId={`lp-dbs-hist-street-${i}`} />
                  <Field label="Town/City" required value={row.town_city} onChange={(v) => setHist(i, "town_city", v)} testId={`lp-dbs-hist-town-${i}`} />
                  <Field label="County" required value={row.county} onChange={(v) => setHist(i, "county", v)} testId={`lp-dbs-hist-county-${i}`} />
                  <Field label="Postcode" required value={row.postcode} onChange={(v) => setHist(i, "postcode", v)} testId={`lp-dbs-hist-postcode-${i}`} />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <FieldWrapper label="Lived there from" required testId={`lp-dbs-hist-from-${i}`}>
                  <MonthYearPicker
                    value={row.from}
                    onChange={(v) => setHist(i, "from", v)}
                    testId={`lp-dbs-hist-from-${i}`}
                  />
                </FieldWrapper>
                <FieldWrapper label="Lived there to" required testId={`lp-dbs-hist-to-${i}`}>
                  <MonthYearPicker
                    value={row.to}
                    onChange={(v) => setHist(i, "to", v)}
                    presentValue="present"
                    presentLabel="Present"
                    testId={`lp-dbs-hist-to-${i}`}
                  />
                </FieldWrapper>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addHist}
          data-testid="lp-dbs-hist-add"
          className="mt-3 px-3 py-1.5 border border-stone-950 bg-white hover:bg-stone-50 rounded-lg text-xs font-bold uppercase tracking-wider"
        >
          + Add another address
        </button>

        {(coverage.errors.length > 0 || coverage.gaps.length > 0) && (
          <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 space-y-1" data-testid="lp-dbs-addr-validation">
            {coverage.errors.map((msg, i) => (<div key={`e-${i}`}>{msg}</div>))}
            {coverage.gaps.length > 0 && (
              <div>
                <div className="font-bold">Address history has gap(s) in the last 5 years:</div>
                <ul className="list-disc pl-5" data-testid="lp-dbs-addr-gaps">
                  {gapMessages.map((g, i) => (<li key={i}>{g}</li>))}
                </ul>
                <div className="text-xs mt-1">Please add address(es) covering this period before submitting.</div>
              </div>
            )}
          </div>
        )}
      </FieldGroup>

      <FieldGroup title="Other Names">
        <p className="text-sm text-stone-700 leading-relaxed max-w-2xl">
          Please provide details of any other names used at any time during your
          lifetime and the dates during which the names were used.
        </p>
        <FieldWrapper label="Have you ever used any other forenames or surnames?" required testId="lp-dbs-has-other-names">
          <div className="flex gap-4 pt-1" role="radiogroup" aria-label="Have you ever used any other forenames or surnames?">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="has_other_names"
                value="yes"
                checked={form.has_other_names === "yes"}
                onChange={() => {
                  setF("has_other_names")("yes");
                  if (otherNames.length === 0) addOtherName();
                }}
                data-testid="lp-dbs-has-other-names-yes"
              />
              Yes
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="has_other_names"
                value="no"
                checked={form.has_other_names === "no"}
                onChange={() => setF("has_other_names")("no")}
                data-testid="lp-dbs-has-other-names-no"
              />
              No
            </label>
          </div>
        </FieldWrapper>
        {form.has_other_names === "yes" && (
          <div className="space-y-3">
            {otherNames.map((row, i) => {
              const currentYear = new Date().getFullYear();
              const years = Array.from({ length: (currentYear - 1900) + 1 }, (_, k) => String(currentYear - k));
              return (
                <div key={i} className="border border-stone-200 rounded-lg p-3 space-y-2" data-testid={`lp-dbs-othername-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500">Other name {i + 1}</div>
                    <button
                      type="button"
                      onClick={() => removeOtherName(i)}
                      data-testid={`lp-dbs-othername-remove-${i}`}
                      className="text-xs text-red-700 underline"
                    >Delete</button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field
                      label="Name"
                      required
                      value={row.name}
                      onChange={(v) => setOtherName(i, "name", v)}
                      testId={`lp-dbs-othername-name-${i}`}
                    />
                    <FieldWrapper label="Type" required testId={`lp-dbs-othername-type-${i}`}>
                      <select
                        value={row.type || ""}
                        onChange={(e) => setOtherName(i, "type", e.target.value)}
                        data-testid={`lp-dbs-othername-type-${i}-select`}
                        className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg bg-white"
                      >
                        <option value="">Select</option>
                        <option value="Forename">Forename</option>
                        <option value="Surname">Surname</option>
                      </select>
                    </FieldWrapper>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FieldWrapper label="From year" required testId={`lp-dbs-othername-from-${i}`}>
                      <select
                        value={row.from_year || ""}
                        onChange={(e) => setOtherName(i, "from_year", e.target.value)}
                        data-testid={`lp-dbs-othername-from-${i}-select`}
                        className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg bg-white"
                      >
                        <option value="">Select year</option>
                        {years.map((y) => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </FieldWrapper>
                    <FieldWrapper label="To year" required testId={`lp-dbs-othername-to-${i}`}>
                      <select
                        value={row.to_year || ""}
                        onChange={(e) => setOtherName(i, "to_year", e.target.value)}
                        data-testid={`lp-dbs-othername-to-${i}-select`}
                        className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg bg-white"
                      >
                        <option value="">Select year</option>
                        {years.map((y) => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </FieldWrapper>
                  </div>
                </div>
              );
            })}
            <button
              type="button"
              onClick={addOtherName}
              data-testid="lp-dbs-othername-add"
              className="mt-1 px-3 py-1.5 border border-stone-950 bg-white hover:bg-stone-50 rounded-lg text-xs font-bold uppercase tracking-wider"
            >
              + Add another name
            </button>
            {otherNameErrors.length > 0 && (
              <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 space-y-1" data-testid="lp-dbs-othername-validation">
                {otherNameErrors.map((msg, i) => (<div key={i}>{msg}</div>))}
              </div>
            )}
          </div>
        )}
      </FieldGroup>

      <FieldGroup title="Convictions">
        <FieldWrapper label="I have unspent criminal convictions or cautions that I need to declare." required testId="lp-dbs-hasconvictions">
          <div className="flex gap-4 pt-1" role="radiogroup" aria-label="I have unspent criminal convictions or cautions that I need to declare.">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="has_convictions"
                value="no"
                checked={form.has_convictions === "no"}
                onChange={() => {
                  // Clear any hidden declaration text when the user
                  // moves back to "No" — defence-in-depth alongside
                  // the backend's own force-null.
                  setForm((s) => ({ ...s, has_convictions: "no", conviction_details: "" }));
                }}
                data-testid="lp-dbs-hasconvictions-no"
              />
              No
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="has_convictions"
                value="yes"
                checked={form.has_convictions === "yes"}
                onChange={() => setF("has_convictions")("yes")}
                data-testid="lp-dbs-hasconvictions-yes"
              />
              Yes
            </label>
          </div>
        </FieldWrapper>
        {form.has_convictions === "yes" && (
          <Field
            label="Please provide details"
            required
            value={form.conviction_details}
            onChange={setF("conviction_details")}
            multiline
            testId="lp-dbs-conviction-details"
          />
        )}
      </FieldGroup>

      <FieldGroup title="Identity documents">
        <p className="text-xs text-stone-500 mb-2">
          Upload three documents. Accepted formats: JPG, PNG, HEIC, WebP, PDF
          (max 12 MB each).{isReopened && " Any documents you've already uploaded are still on file — only re-upload if you want to replace them."}
        </p>
        {[1, 2, 3].map((n) => {
          const key = `doc${n}`;
          const existing = existingUploads[key];
          return (
            <DocSlot
              key={n}
              slot={n}
              label={n === 1 ? "Document 1 (Identity)" : `Document ${n}`}
              docType={form[`${key}_type`]}
              setDocType={setF(`${key}_type`)}
              types={(n === 1 ? docTypes?.doc1 : docTypes?.doc23) || []}
              file={files[key]}
              setFile={(f) => { touch(); setFiles((s) => ({ ...s, [key]: f })); }}
              existingFilename={existing?.filename}
              existingType={existing?.type}
            />
          );
        })}
      </FieldGroup>

      {err && <div className="text-sm text-red-700 whitespace-pre-wrap" data-testid="lp-dbs-error">{err}</div>}

      <button
        type="submit"
        disabled={!canSubmit}
        data-testid="lp-dbs-submit"
        className="w-full md:w-auto px-6 py-3 bg-stone-950 hover:bg-stone-800 text-[#dddd16] rounded-lg text-sm font-bold uppercase tracking-widest disabled:opacity-40"
      >
        {busy ? "Submitting…" : "Submit DBS application"}
      </button>
      {!canSubmit && (missingPersonal.length > 0 || filesMissing.length > 0) && (
        <div className="text-xs text-stone-500" data-testid="lp-dbs-submit-hint">
          Still needed: {[...missingPersonal, ...filesMissing].slice(0, 5).join(", ")}
          {(missingPersonal.length + filesMissing.length) > 5 && ` +${(missingPersonal.length + filesMissing.length) - 5} more`}
        </div>
      )}
      <p className="text-xs text-stone-500 max-w-2xl">
        Once submitted, this form is locked and cannot be edited. Please
        double-check every field before hitting Submit.
      </p>
    </form>
  );
}

// A tiny wrapper for label + required marker + arbitrary children — used
// for the two MonthYearPicker fields where we can't reuse ``Field``.
function FieldWrapper({ label, required, children, testId }) {
  return (
    <label className="block" data-testid={testId ? `${testId}-wrapper` : undefined}>
      <span className="block text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">
        {label}{required && <span className="text-red-600 ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}


// ================================================================
// 6.7 Direct Debit
// ================================================================
export function DirectDebitSection({ slug, launchPortal, onSubmitted }) {
  const submittedAt = launchPortal.direct_debit_submitted_at;
  const submitted = !!submittedAt;
  const [sortCode, setSortCode] = useState("");
  const [account, setAccount] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await submitJson(slug, "direct-debit", {
        sort_code: sortCode, account_number: account,
      });
      onSubmitted && onSubmitted();
    } catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div data-testid="launch-section-direct-debit">
        <p className="text-stone-700 leading-relaxed max-w-2xl">
          Your bank details have been submitted securely. HQ will use these to
          set up your monthly royalty payment. Only Paul and Sandra can access
          the full details.
        </p>
        <SubmittedLock text={`Submitted ${formatDate(submittedAt)} — this form is now locked.`} testId="lp-dd-locked" />
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4 max-w-2xl" data-testid="launch-section-direct-debit">
      <p className="text-stone-700 leading-relaxed">
        Please provide your bank details so HQ can set up your monthly royalty
        Direct Debit. These details are private — only Paul and Sandra will be
        able to view them once submitted.
      </p>
      <Field label="Sort Code (e.g. 12-34-56)" value={sortCode} onChange={setSortCode} testId="lp-dd-sort" />
      <Field label="Account Number (6–10 digits)" value={account} onChange={setAccount} testId="lp-dd-account" />
      {err && <div className="text-sm text-red-700" data-testid="lp-dd-error">{err}</div>}
      <button
        type="submit"
        disabled={busy}
        data-testid="lp-dd-submit"
        className="px-5 py-2.5 bg-stone-950 hover:bg-stone-800 text-[#dddd16] rounded-lg text-sm font-bold uppercase tracking-wider disabled:opacity-40"
      >
        {busy ? "Submitting…" : "Submit Bank Details"}
      </button>
    </form>
  );
}


// ================================================================
// 6.8 Comfort Zone — informational + HQ-provided join link
// ================================================================
export function ComfortZoneSection({ launchPortal }) {
  const link = launchPortal.comfort_zone_join_link;
  return (
    <div className="space-y-3 max-w-2xl" data-testid="launch-section-comfort-zone">
      <p className="text-stone-700 leading-relaxed">
        The <b>Creative Mojo Comfort Zone</b> is our private Facebook community
        for franchisees. It&apos;s where the network shares wins, asks questions,
        and supports each other day-to-day. Join us using the link below.
      </p>
      {link ? (
        <a
          href={link}
          target="_blank"
          rel="noreferrer"
          data-testid="lp-comfort-zone-join"
          className="inline-flex items-center gap-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold uppercase tracking-wider rounded-lg"
        >
          Join the Comfort Zone <ExternalLink className="w-3.5 h-3.5" />
        </a>
      ) : (
        <div className="text-sm text-stone-500 italic" data-testid="lp-comfort-zone-pending">
          HQ is preparing your invitation — the join link will appear here shortly.
        </div>
      )}
    </div>
  );
}


// ------------------------------------------------------------------ apron colour

const APRON_COLOURS = [
  "Apple Green", "Aubergine", "Black", "Burgundy", "Denim Black",
  "Dark Grey", "Hot Pink", "Mid Blue", "Marine Blue", "Denim Indigo",
  "Natural", "Navy", "Oasis Green", "Olive", "Purple",
  "Red", "Sage", "Silver", "Steel", "White",
];

const APRON_IMAGE_URL =
  "https://customer-assets-jt897jd0.emergentagent.net/job_e6cd30ad-80d0-4b7a-b10c-07bd4f1983f9/artifacts/65zf3p92_Aprons%202026.png";

export function ApronColourSection({ launchPortal, slug, onRefresh }) {
  const submitted = launchPortal.apron_colour;
  const [choice, setChoice] = React.useState(submitted?.colour || "");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const locked = Boolean(submitted?.submitted_at);

  const handleSubmit = async () => {
    if (!choice) { setErr("Please pick a colour first."); return; }
    setBusy(true); setErr(null);
    try {
      await submitJson(slug, "apron-colour", { colour: choice });
      onRefresh && (await onRefresh());
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save your choice. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl" data-testid="launch-section-apron-colour">
      <p className="text-stone-700 leading-relaxed">
        Choose the colour of your <b>Creative Mojo branded apron</b>. Take a look
        at the range below and pick your favourite from the dropdown.
      </p>
      <img
        src={APRON_IMAGE_URL}
        alt="Creative Mojo branded apron colour range"
        className="w-full rounded-lg border border-stone-200"
        data-testid="lp-apron-colour-image"
      />
      {locked ? (
        <div
          className="rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-900 text-sm p-3"
          data-testid="lp-apron-colour-locked"
        >
          <div className="font-bold">You&apos;ve chosen: {submitted.colour}</div>
          <div className="text-xs opacity-75 mt-0.5">
            Submitted — this selection can&apos;t be changed here. Please speak
            to HQ if you need to update it.
          </div>
        </div>
      ) : (
        <>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">
              Apron Colour
            </span>
            <select
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
              data-testid="lp-apron-colour-select"
              className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg bg-white focus:outline-none focus:border-stone-900"
            >
              <option value="">— Select a colour —</option>
              {APRON_COLOURS.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </label>
          {err && (
            <div className="text-sm text-red-700" data-testid="lp-apron-colour-error">{err}</div>
          )}
          <button
            onClick={handleSubmit}
            disabled={busy || !choice}
            data-testid="lp-apron-colour-submit"
            className="inline-flex items-center gap-2 px-4 py-2 bg-stone-900 hover:bg-stone-800 disabled:bg-stone-300 text-white text-sm font-bold uppercase tracking-wider rounded-lg"
          >
            {busy ? "Saving…" : "Submit"}
          </button>
        </>
      )}
    </div>
  );
}


// ------------------------------------------------------------------ tiny helpers

function Field({ label, value, onChange, type = "text", disabled = false, multiline = false, testId, required = false }) {
  const cls = "w-full px-3 py-2 text-sm border border-stone-300 rounded-lg bg-white focus:outline-none focus:border-stone-900 disabled:bg-stone-50 disabled:text-stone-500";
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">
        {label}{required && <span className="text-red-600 ml-1">*</span>}
      </span>
      {multiline ? (
        <textarea
          rows={3}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          data-testid={testId}
          className={cls}
        />
      ) : (
        <input
          type={type}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          data-testid={testId}
          className={cls}
        />
      )}
    </label>
  );
}

function FieldGroup({ title, children }) {
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-bold uppercase tracking-widest text-stone-950">{title}</h4>
      {children}
    </div>
  );
}

function KV({ label, value, mono = false }) {
  return (
    <div className="flex gap-2 items-baseline">
      <span className="text-[10px] uppercase tracking-widest font-bold text-stone-500 w-32 shrink-0">{label}</span>
      <span className={"tabular-nums text-stone-900 " + (mono ? "font-mono" : "")}>{value || "—"}</span>
    </div>
  );
}

function DocSlot({ slot, label, docType, setDocType, types, file, setFile, existingFilename, existingType }) {
  return (
    <div className="rounded-lg border border-stone-200 p-3 space-y-2 bg-stone-50">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest font-bold text-stone-500">{label}<span className="text-red-600 ml-1">*</span></div>
        {existingFilename && !file && (
          <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300 rounded-full px-2 py-0.5" data-testid={`lp-dbs-doc${slot}-existing`}>
            On file
          </span>
        )}
      </div>
      <select
        value={docType || ""}
        onChange={(e) => setDocType(e.target.value)}
        data-testid={`lp-dbs-doc${slot}-type`}
        className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg bg-white"
      >
        <option value="">— Select document type —</option>
        {types.map((t) => <option key={t} value={t}>{t}</option>)}
      </select>
      {existingFilename && !file && (
        <div className="text-xs text-stone-600" data-testid={`lp-dbs-doc${slot}-existing-name`}>
          Currently on file: <span className="font-mono">{existingFilename}</span>{existingType ? ` (${existingType})` : ""}
        </div>
      )}
      <input
        type="file"
        accept="image/jpeg,image/png,image/heic,image/webp,application/pdf"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        data-testid={`lp-dbs-doc${slot}-file`}
        className="block w-full text-sm text-stone-700"
      />
      {file && (
        <div className="text-xs text-stone-600 italic">
          {existingFilename ? "Will replace: " : ""}{file.name} — {(file.size / 1024).toFixed(0)} KB
        </div>
      )}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
